import {
  format as dateFormatter,
  addDays,
} from 'date-fns';
import { TZDate } from '@date-fns/tz';

import {
  ORDER_TYPES,
  INOVICE_ITEM_TYPES,
  TRANSACTION_ITEM_TYPES,
  COUPON_TARGET_TYPES,
} from './constants';

interface Variant {
  variant: string;
  price: number | string;
  inventoryOnly?: boolean;
}

interface PricedOption {
  price: number | string;
}

interface Product {
  id?: string | number;
  name: string;
  vendor?: string;
  variants: Variant[];
  customizations: Record<string, PricedOption>;
  shippingWindow?: Record<string, PricedOption>;
  collectionIds?: Array<string | number>;
}

interface BarcodeItem {
  barcode: string;
  itemId?: number | string;
}

interface OrderItem {
  id: string;
  size?: string;
  color?: string;
  amounts: number;
  unitPrice: number;
  product?: Product;
  productId?: string | number;
  fromInventory?: boolean;
  selectedCustomizations?: Record<string, unknown>;
  selectedShippingWindow?: string;
  barcodeItems?: BarcodeItem[];
}

interface AdditionalCharge {
  _id: { toString(): string };
  price: number | string;
}

interface AppliedCoupon {
  discount: number;
  targetType: string;
  targetIds?: unknown[];
}

interface TransactionItem {
  type: string;
  itemId?: string;
  amount?: number | string;
}

interface Transaction {
  amount?: number | string;
  items?: TransactionItem[];
}

interface Order {
  items: OrderItem[];
  additionalCharges: AdditionalCharge[];
  couponDiscount?: number;
  appliedCoupons?: AppliedCoupon[];
  barcodeItems: BarcodeItem[];
  transactions?: Transaction[];
}

interface InvoiceItem {
  type: string;
  value: string;
}

interface Invoice {
  items: InvoiceItem[];
}

interface OrderUser {
  isCustomer?: boolean;
  isAdmin?: boolean;
  isStaff?: boolean;
  isSalesperson?: boolean;
}

interface AppConfig {
  shippingDelayInWeeks: number | string;
  shippingDelayFrom: string | number | Date;
  shippingDelayTo?: string | number | Date;
  timeZone?: string;
  wearDateBufferDays?: number | string;
  customizationConfirmDays?: number | string;
}

interface ParsedBarcode {
  [key: string]: number | string | null;
}

function calcCartItemWholesalePrice(item: OrderItem, product: Product): number {
  const find = product.variants.find((v) => v.variant === `${item.size} / ${item.color}`);
  return find
    ? Number(find.price)
    : 0;
}

function calcCartItemUnitPrice(item: OrderItem, product?: Product): number {
  const prod = (product || item.product) as Product;

  let unitPrice = calcCartItemWholesalePrice(item, prod);
  if (item.selectedCustomizations) {
    Object.keys(item.selectedCustomizations)
      .filter((k) => item.selectedCustomizations![k])
      .forEach((k) => {
        unitPrice += Number(prod.customizations[k]!.price);
      });
  }

  // WORKAROUND compatible with the order created with a product without shippingWindow
  if (!item.fromInventory && prod.shippingWindow) {
    const current = prod.shippingWindow[item.selectedShippingWindow as string];
    if (current) {
      unitPrice += Number(current.price);
    }
  }

  return unitPrice;
}

interface OrderTotalOptions {
  vendor?: string;
  isBulkOrder?: boolean;
}

function calcOrderTotalPrice(order: Order, options: OrderTotalOptions = {}): number {
  const { vendor, isBulkOrder } = options;
  let total = 0;

  const { items, additionalCharges, couponDiscount } = order;
  items.forEach((item) => {
    if (vendor && item.product!.vendor !== vendor) {
      return;
    }

    const { amounts } = item;
    const unitPrice = calcCartItemUnitPrice(item);
    total += (unitPrice * amounts);
  });

  // add additional charges
  if (additionalCharges && !isBulkOrder) {
    additionalCharges.forEach((ac) => {
      total += Number(ac.price);
    });
  }

  // subtract coupon discount — only for full-order totals, not vendor- or bulk-scoped calls
  if (couponDiscount && couponDiscount > 0 && !vendor && !isBulkOrder) {
    total -= couponDiscount;
  }

  return total;
}

/**
 * Distribute applied coupon discounts across order items to get each item's
 * effective (post-discount) total price. Handles CART, PRODUCT, and COLLECTION targets.
 */
function buildEffectivePriceMap(
  items: OrderItem[],
  appliedCoupons?: AppliedCoupon[],
): Record<string, number> {
  const map: Record<string, number> = {};
  items.forEach((item) => {
    map[item.id] = item.unitPrice * item.amounts;
  });

  if (!appliedCoupons || appliedCoupons.length === 0) return map;

  const grossTotal = Object.values(map).reduce((sum, v) => sum + v, 0);
  const totalCouponDiscount = appliedCoupons.reduce((sum, c) => sum + (c.discount || 0), 0);
  const targetSum = Math.max(0, grossTotal - totalCouponDiscount);

  appliedCoupons.forEach((coupon) => {
    let matchingItems: OrderItem[];

    if (coupon.targetType === COUPON_TARGET_TYPES.CART) {
      matchingItems = items;
    } else if (coupon.targetType === COUPON_TARGET_TYPES.PRODUCT) {
      matchingItems = items.filter((item) =>
        (coupon.targetIds || []).includes(item.productId || (item.product && item.product.id)),
      );
    } else if (coupon.targetType === COUPON_TARGET_TYPES.COLLECTION) {
      matchingItems = items.filter((item) => {
        const collectionIds = (item.product && item.product.collectionIds) || [];
        return collectionIds.some((cid) => (coupon.targetIds || []).includes(cid));
      });
    } else {
      return;
    }

    const matchingGross = matchingItems.reduce(
      (sum, item) => sum + item.unitPrice * item.amounts, 0,
    );
    if (matchingGross === 0) return;

    matchingItems.forEach((item) => {
      const itemGross = item.unitPrice * item.amounts;
      const itemDiscount = (itemGross / matchingGross) * coupon.discount;
      map[item.id] = Math.max(0, map[item.id]! - itemDiscount);
    });
  });

  // Normalize: clamp per-item to avoid sum(map) drifting above targetSum due to over-discount
  const effectiveSum = Object.values(map).reduce((sum, v) => sum + v, 0);
  if (effectiveSum > targetSum + 0.001) {
    const scaleFactor = targetSum / effectiveSum;
    Object.keys(map).forEach((id) => {
      map[id] = map[id]! * scaleFactor;
    });
  }

  return map;
}

function calcInvoiceTotal(
  invoice: Invoice,
  order: Order,
  additionalAdditionalCharges?: AdditionalCharge[],
): number {
  const additionalCharges = order.additionalCharges.map((ac) => ({
    ...ac,
    // convert ObjectId to string on _id field
    _id: ac._id.toString(),
  }));

  if (additionalAdditionalCharges) {
    // additionalAdditionalCharges is the additinalCharges not have been saved yet
    additionalAdditionalCharges.forEach((aac) => additionalCharges.push({
      ...aac,
      _id: aac._id.toString(),
    }));
  }

  const effectivePriceMap = buildEffectivePriceMap(order.items, order.appliedCoupons);

  return invoice.items.reduce((acc, { type, value }) => {
    if (type === INOVICE_ITEM_TYPES.BARCODE_ITEM) {
      const barcode = order.barcodeItems.find((bi) => bi.barcode === value);
      const find = order.items.find((itm) => {
        const { fromInventory } = itm;
        const itemBarcodes = itm.barcodeItems && itm.barcodeItems.length ? itm.barcodeItems.map((bi) => bi.barcode) : [];
        if (fromInventory) {
          return itemBarcodes.some((b) => {
            const p = parseBarcode(b);
            return (p!.itemId === barcode!.itemId);
          });
        }

        return (itm.id === barcode!.itemId);
      });
      if (find) {
        const effectiveTotal = effectivePriceMap[find.id] || 0;
        const effectiveUnitPrice = find.amounts > 0 ? effectiveTotal / find.amounts : 0;
        acc += effectiveUnitPrice;
      }
    } else if (type === INOVICE_ITEM_TYPES.ADDITIONAL_CHARGE) {
      const find = additionalCharges.find((ac) => ac._id === value);
      if (find) acc += Number(find.price);
    }
    return acc;
  }, 0);
}

// Calculate how much has been paid through transactions for each order item
function calcItemPaidAmounts(order: Order): Record<string, number> {
  const transactions = order.transactions || [];
  const itemPaidMap: Record<string, number> = {};

  transactions.forEach((transaction) => {
    (transaction.items || []).forEach((transItem) => {
      if (transItem.type === TRANSACTION_ITEM_TYPES.ORDER_ITEM) {
        const itemId = transItem.itemId as string;
        if (!itemPaidMap[itemId]) {
          itemPaidMap[itemId] = 0;
        }
        itemPaidMap[itemId] += Number(transItem.amount);
      }
    });
  });

  return itemPaidMap;
}

// Calculate total transaction amount
function calcTransactionTotal(
  transaction: Transaction,
  _order?: Order,
  _additionalAdditionalCharges?: AdditionalCharge[],
): number {
  // Simply sum all item amounts from the transaction
  // Each item should have its amount stored at transaction creation time
  return (transaction.items || []).reduce((acc, transItem) => {
    acc += Number(transItem.amount || 0);
    return acc;
  }, 0);
}

// UTC instant of midnight of `date`'s calendar day in `timeZone`.
// Without a timeZone the day boundary is the runtime's — only acceptable for
// viewer-local UI; business-day logic must always pass the market timezone.
function startOfDayInTZ(timeZone?: string, date?: string | number | Date): Date {
  if (!timeZone) {
    const d = date !== undefined ? new Date(date) : new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }
  const d = date !== undefined
    ? new TZDate(new Date(date).getTime(), timeZone)
    : TZDate.tz(timeZone);
  d.setHours(0, 0, 0, 0);
  return new Date(d.getTime());
}

// UTC instant of 23:59:59.999 of that day — for inclusive range filters.
function endOfDayInTZ(timeZone?: string, date?: string | number | Date): Date {
  if (!timeZone) {
    const d = date !== undefined ? new Date(date) : new Date();
    d.setHours(23, 59, 59, 999);
    return d;
  }
  const d = date !== undefined
    ? new TZDate(new Date(date).getTime(), timeZone)
    : TZDate.tz(timeZone);
  d.setHours(23, 59, 59, 999);
  return new Date(d.getTime());
}

// Calendar-day arithmetic in the market timezone. addDays/addWeeks on a plain
// Date advance the absolute timeline, so crossing a DST transition drifts the
// result off market midnight by an hour; arithmetic on a TZDate stays on the
// market calendar.
function addDaysInTZ(
  timeZone: string | undefined,
  date: string | number | Date,
  amount: number,
): Date {
  const base = timeZone
    ? new TZDate(new Date(date).getTime(), timeZone)
    : new Date(date);
  return new Date(addDays(base, amount).getTime());
}

// Re-anchor a date picked in the browser's local calendar (a DatePicker value
// is browser-local midnight of the picked day) to the same calendar day in
// the market timezone. Returns market NOON, not midnight, so the stored
// instant round-trips to the same calendar day in picker displays for any
// browser within ±12h of the market.
function anchorDayToTZ(timeZone: string | undefined, picked: string | number | Date): Date {
  const local = new Date(picked);
  if (!timeZone) {
    return local;
  }
  const d = new TZDate(local.getFullYear(), local.getMonth(), local.getDate(), timeZone);
  d.setHours(12, 0, 0, 0);
  return new Date(d.getTime());
}

// Whole market-calendar days from today to `date`'s day: 0 on the day itself,
// negative once it has passed. Counts calendar days, not 24h blocks, so a
// deadline at the end of day N+5 is "5 days" away on day N.
function calendarDaysUntilInTZ(
  timeZone: string | undefined,
  date: string | number | Date,
  now: string | number | Date = Date.now(),
): number {
  const diff = startOfDayInTZ(timeZone, date).getTime() - startOfDayInTZ(timeZone, now).getTime();
  // round: a DST transition makes one day 23h or 25h
  return Math.round(diff / (24 * 60 * 60 * 1000));
}

const getDateOnly = (date?: string | number | Date, timeZone?: string): Date => (
  startOfDayInTZ(timeZone, date)
);

interface MinWearDateInput {
  shippingWindowInWeeks?: number;
  shippingDelayInWeeks?: number;
  availableDate?: string | number | Date;
  fromInventory?: boolean;
  inStock?: boolean;
  timeZone?: string | undefined;
}

function calcMinWearDate({
  shippingWindowInWeeks,
  shippingDelayInWeeks,
  availableDate,
  fromInventory,
  inStock,
  timeZone,
}: MinWearDateInput): Date {
  if (inStock) {
    return new Date();
  }

  const startDate = (fromInventory)
    ? new Date(availableDate as string | number | Date)
    : new Date();


  const _shippingWindowInWeeks = (inStock || fromInventory)
    ? 0
    : shippingWindowInWeeks;


  const _shippingDelayInWeeks = (inStock || fromInventory)
    ? 0
    : shippingDelayInWeeks;

  return addDaysInTZ(
    timeZone,
    getDateOnly(startDate, timeZone),
    ((_shippingDelayInWeeks as number) + (_shippingWindowInWeeks as number)) * 7,
  );
}

const FAR_FUTURE = new Date(Date.UTC(2100, 0, 1));

// return the shipping delay in weeks
// return 0 if current time is out of the delay date range
// The from/to bounds are calendar days in the market timezone: the delay is
// active from the start of the `from` day through the end of the `to` day.
function getValidShippingDelayInWeeks(appConfig: AppConfig): number {
  const shippingDelayInWeeks = Number(appConfig.shippingDelayInWeeks);
  const now = new Date();
  const from = startOfDayInTZ(appConfig.timeZone, appConfig.shippingDelayFrom);
  const to = appConfig.shippingDelayTo
    ? addDaysInTZ(appConfig.timeZone, startOfDayInTZ(appConfig.timeZone, appConfig.shippingDelayTo), 1)
    : FAR_FUTURE;
  return (shippingDelayInWeeks > 0 && now > from && now < to)
    ? shippingDelayInWeeks
    : 0;
}

// return the admin-configured number of extra days required between the
// estimated ship date and the wear date (warehouse prep margin)
// return 0 if unset or invalid
function getWearDateBufferDays(appConfig: Pick<AppConfig, 'wearDateBufferDays'>): number {
  const days = Math.floor(Number(appConfig.wearDateBufferDays));
  return (Number.isFinite(days) && days > 0)
    ? days
    : 0;
}

// return the admin-configured number of days a customer has to confirm
// customized items; default 5, clamped to 1–60
function getCustomizationConfirmDays(appConfig: Pick<AppConfig, 'customizationConfirmDays'>): number {
  const days = Math.floor(Number(appConfig.customizationConfirmDays));
  return (Number.isFinite(days) && days >= 1)
    ? Math.min(days, 60)
    : 5;
}

// Order mail goes out only when the sender account is fully configured
// (MailService#ensure and the admin settings banner share this check).
function isMailConfigured(
  appConfig: { senderEmail?: unknown; senderEmailPassword?: unknown; senderName?: unknown },
): boolean {
  return !!(appConfig.senderEmail && appConfig.senderEmailPassword && appConfig.senderName);
}

// An item is customized when a selected customization key points at a real
// option of the product. Never trust the client-set item `type`.
function isCustomizedItem(
  item: Pick<OrderItem, 'selectedCustomizations'>,
  product?: { customizations?: unknown } | null,
): boolean {
  const options = product?.customizations;
  if (!item.selectedCustomizations || !Array.isArray(options)) {
    return false;
  }
  return Object.keys(item.selectedCustomizations)
    .some((k) => item.selectedCustomizations![k] && /^(0|[1-9]\d*)$/.test(k) && options[Number(k)] != null);
}

interface CurrencyOptions {
  locale?: string;
  currency?: string;
}

const currencyFormatterCache = new Map<string, Intl.NumberFormat>();
function formatCurrency(value: number, options: CurrencyOptions = {}): string {
  const locale = options.locale || 'en-US';
  const currency = options.currency || 'USD';
  const key = `${locale}:${currency}`;
  if (!currencyFormatterCache.has(key)) {
    currencyFormatterCache.set(key, new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
    }));
  }
  return currencyFormatterCache.get(key)!.format(value);
}

const DATE_FORMAT_STRING = 'MMM-d-yyyy';

interface FormatDateOptions {
  locale?: string | undefined;
  timeZone?: string | undefined;
}

// options: { locale, timeZone } — a locale switches to Intl.DateTimeFormat for
// locale-aware output; a timeZone alone keeps the US format but resolves the
// calendar day in that zone. No options preserves the original behavior.
function formatDate(
  input: string | number | Date,
  fallbackString?: string,
  options: FormatDateOptions = {},
): string {
  const d = new Date(input);
  if (d.getDate() > 0) {
    if (options.locale) {
      return new Intl.DateTimeFormat(options.locale, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        ...(options.timeZone ? { timeZone: options.timeZone } : {}),
      }).format(d);
    }
    const target = options.timeZone ? new TZDate(d.getTime(), options.timeZone) : d;
    return dateFormatter(target, DATE_FORMAT_STRING);
  }
  return fallbackString || '';
}

// Date + time variant (e.g. "Jun 11, 2026, 3:45 PM"). Defaults to en-US so
// output is consistent across browsers/servers; pass locale/timeZone from
// user or market context for localized rendering.
function formatDateTime(
  input: string | number | Date,
  fallbackString?: string,
  options: FormatDateOptions = {},
): string {
  const d = new Date(input);
  if (d.getDate() > 0) {
    return new Intl.DateTimeFormat(options.locale || 'en-US', {
      dateStyle: 'medium',
      timeStyle: 'short',
      ...(options.timeZone ? { timeZone: options.timeZone } : {}),
    }).format(d);
  }
  return fallbackString || '';
}

function formatTrackingNumber(trackingNumber: string): { carrier: string; link: string | null } {
  const regexes: Record<string, RegExp> = {
    ups: /\b(1Z ?[0-9A-Z]{3} ?[0-9A-Z]{3} ?[0-9A-Z]{2} ?[0-9A-Z]{4} ?[0-9A-Z]{3} ?[0-9A-Z])\b/g, // UPS starts with '1Z' followed by 16 digits/letters
    fedex: /\b((96|98)?[0-9]{20})\b/g, // FedEx has varying lengths (12 to 20 digits)
    usps: /\b(91\d{18}|\d{20}|\b\d{30}\b|\b82\d{18})\b/g, // USPS has two common patterns of varying lengths
    dhl: /\b([0-9]{10}|[0-9]{12}|[0-9]{13}|[0-9]{14})\b/g, // DHL is typically 10 or 11 digits
  };

  const links: Record<string, string> = {
    ups: 'https://www.ups.com/track?loc=en_US&tracknum=',
    fedex: 'https://www.fedex.com/fedextrack/?trknbr=',
    usps: 'https://tools.usps.com/go/TrackConfirmAction?tLabels=',
    dhl: 'https://www.dhl.com/en-us/home/tracking/tracking-express.html?tracking-id=',
  };


  for (const carrier in regexes) {
    if (regexes[carrier]!.test(trackingNumber)) {
      return {
        carrier,
        link: links[carrier] + trackingNumber,
      };
    }
  }

  return { carrier: 'Unknown', link: null };
}

function determineOrderType(orderByUser: OrderUser, orderAsUser: OrderUser): number | null {
  if (orderByUser.isCustomer) {
    return ORDER_TYPES.CUSTOMER_ORDER;
  }

  if (!orderByUser || !orderAsUser) {
    // invalid input, should handle this error
    return null;
  }

  // order by admin / staff / salesperson
  if (orderAsUser.isCustomer) {
    // order by staff or admin or salesperson
    return ORDER_TYPES.ADMIN_ORDER;
  }

  if (orderByUser.isAdmin
    && orderAsUser.isAdmin) {
    return ORDER_TYPES.INVENTORY_ORDER;
  }

  // invalid input, should handle this error
  return null;
}

/*
  return null if input valule is not a string
  return null if any part was failed to parse
*/
function parseBarcode(str: unknown): ParsedBarcode | null {
  if (typeof str !== 'string') {
    return null;
  }

  const barcode = str.replace(/[^-0-9a-z]/g, '');

  // 196-1-na-na-37
  const parts = barcode.split('-');
  if (parts.filter((p) => p).length !== 5) {
    return null;
  }

  // all fields should be a number
  // all fields should gte than 0
  const parsed = ['itemId', 'itemIndex', 'orderId', 'bulkOrderId', 'inventoryOrderId']
    .reduce<ParsedBarcode | null>((result, curr, idx) => {
      if (!result) {
        return result;
      }

      const val = parts[idx];
      const nval = Number(val);
      if (val === 'na') {
        result[curr] = null;
      } else if (nval > 0) {
        result[curr] = nval;
      } else {
        result = null;
      }

      return result;
    }, { barcode });

  if (!parsed) {
    return null;
  }

  // at least one of ['bulkOrderId', 'inventoryOrderId'] should available
  if (['bulkOrderId', 'inventoryOrderId'].every((oid) => !parsed[oid])) {
    return null;
  }

  // if bulk order id exists the order id should be also exists
  if (parsed.bulkOrderId && !parsed.orderId) {
    return null;
  }

  // orderId is required for a bulkOrder
  if (parsed.bulkOrderId && !parsed.orderId) {
    return null;
  }

  return parsed;
}

export {
  calcCartItemWholesalePrice,
  calcCartItemUnitPrice,
  calcOrderTotalPrice,
  buildEffectivePriceMap,
  calcInvoiceTotal,
  calcItemPaidAmounts,
  calcTransactionTotal,
  formatCurrency,
  formatTrackingNumber,
  getDateOnly,
  startOfDayInTZ,
  endOfDayInTZ,
  addDaysInTZ,
  anchorDayToTZ,
  calendarDaysUntilInTZ,
  calcMinWearDate,
  getValidShippingDelayInWeeks,
  getWearDateBufferDays,
  getCustomizationConfirmDays,
  isCustomizedItem,
  isMailConfigured,
  DATE_FORMAT_STRING,
  formatDate,
  formatDateTime,
  determineOrderType,
  parseBarcode,
  type AppConfig,
  type Product,
  type OrderItem,
};
